import { PermissionsEnum } from '~/constants/Permission';
import { PUBLIC_PATHS } from '~/constants/publicPaths';
import type { AccountType, Permissions } from '~/types/code/code';

/**
 * 画面とアクセスに必要な権限のマッピング
 */
const PERMISSION_MAP: Record<string, Permissions[]> = {
  // 荷役作業受付
  '/work': [
    PermissionsEnum.CARGO_ENTRY,
  ],
  // 列車運行状況
  '/station/delay': [
    PermissionsEnum.TRAIN_STATUS,
  ],
  // 駅からのお知らせ
  '/station/notification': [
    PermissionsEnum.STATION_NOTICE,
  ],
  // アカウント承認
  '/account/approval': [
    PermissionsEnum.ACC_APPROVE,
  ],
  '/account/application/detail': [
    PermissionsEnum.ACC_APPROVE,
  ],
  // アカウント管理
  '/account/list': [
    PermissionsEnum.ACC_MGMT_MOD_DEL,
  ],
  '/account/detail': [
    PermissionsEnum.ACC_MGMT_MOD_DEL,
  ],
  '/account/edit': [
    PermissionsEnum.ACC_MGMT_MOD_DEL,
  ],
  // 業務管理 - ログイン用QRコード出力
  '/business/output-login-card': [
    PermissionsEnum.QR_OUTPUT,
  ],
};

/**
 * 自身のアカウント変更詳細画面のパスと対応するログインユーザのアカウント区分のマッピング
 */
const OWN_ACCOUNT_CHANGE_DETAIL_PATH_MAP: Record<string, AccountType> = {
  '/account/application/detail/truck-driver/me': 'DRIVER',
  '/account/application/detail/guest-truck-driver/me': 'DRIVER_GUEST',
  '/account/application/detail/office/me': 'OFFICE',
  '/account/application/detail/jrf/me': 'JRF',
  '/account/application/detail/partner/me': 'PARTNER',
  '/account/application/detail/corporation/me': 'CORPORATION',
};

/**
 * 自身のアカウント変更申請画面のパスと対応するログインユーザのアカウント区分のマッピング
 */
const OWN_ACCOUNT_CHANGE_APPLICATION_PATH_MAP: Record<string, AccountType> = {
  '/account/application/change/truck-driver': 'DRIVER',
  '/account/application/change/guest-truck-driver': 'DRIVER_GUEST',
  '/account/application/change/office': 'OFFICE',
  '/account/application/change/jrf': 'JRF',
  '/account/application/change/partner': 'PARTNER',
  '/account/application/change/corporation': 'CORPORATION',
};

export default defineNuxtRouteMiddleware(async (to) => {
  // 認証不要のパスは処理をスキップ
  if (PUBLIC_PATHS.includes(to.path)) {
    return;
  }

  const { loginUser } = useLoginUserStore();

  // ログインユーザーが取得できない場合はスキップ（auth.global.tsで処理される）
  if (!loginUser) {
    return;
  }

  // 自身のアカウント変更詳細画面の場合は、ログインユーザのアカウント区分と対応するパスかどうかを確認
  for (const [path, accountType] of Object.entries(OWN_ACCOUNT_CHANGE_DETAIL_PATH_MAP)) {
    if (to.path === path) {
      if (loginUser.accountType !== accountType) {
        const logger = useLogger('0-00');
        logger.warn(`権限不足: ${to.path} (必要なアカウント区分: ${accountType})`);
        return navigateTo('/');
      }
      // アカウント区分が合っている場合はOK
      return;
    }
  }

  // 自身のアカウント変更申請画面の場合も同様に確認
  for (const [path, accountType] of Object.entries(OWN_ACCOUNT_CHANGE_APPLICATION_PATH_MAP)) {
    if (to.path === path) {
      // Webアカウント申請（変更）権限も必要
      if (!loginUser.permissions?.includes(PermissionsEnum.WEB_ACC_APPLY_MOD)) {
        const logger = useLogger('0-00');
        logger.warn(`権限不足: ${to.path} (必要な権限: ${PermissionsEnum.WEB_ACC_APPLY_MOD})`);
        return navigateTo('/');
      }

      if (loginUser.accountType !== accountType) {
        const logger = useLogger('0-00');
        logger.warn(`権限不足: ${to.path} (必要なアカウント区分: ${accountType})`);
        return navigateTo('/');
      }
      // アカウント区分が合っている場合はOK
      return;
    }
  }

  // パスに対する権限チェック
  for (const [path, requiredPermissions] of Object.entries(PERMISSION_MAP)) {
    if (to.path.startsWith(path)) {
      // いずれかの権限を持っているか確認（OR条件）
      const hasPermission = requiredPermissions.some((permission) =>
        loginUser.permissions?.includes(permission),
      );

      if (!hasPermission) {
        const logger = useLogger('0-00');
        logger.warn(`権限不足: ${to.path} (必要な権限: ${requiredPermissions.join(', ')})`);
        return navigateTo('/');
      }

      // 権限がある場合は処理を抜ける
      break;
    }
  }
});
